import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { Row, Col, Button, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { useCartStore, useCompanyStore } from 'stores';
import { calculateShippingFee } from 'components/cart/utils';
import useDebouncedCallback from 'utils/customHooks/useDebouncedCallback';
//api
import { getCountries, getProvinces } from 'api/company';
import { getLimitedDeliveries } from 'api/transport';
//components
import { InputCustom } from 'components/input';

const AddressForm = ({
  data,
  onChange,
  onCancel,
  onSave,
  disableBtnSave,
  hideCancelButton = false,
  countries,
  additionalInfoHeader = null,
  isShippingAddressDetails = false,
  isBillingAddressDetails = false,
}) => {
  const { t } = useTranslation(['backoffice', 'shipping']);
  const [provinces, setProvinces] = useState([]);
  const [limitedDeliveries, setLimitedDeliveries] = useState([]);
  const [isLimitedDeliveryMatch, setIsLimitedDeliveryMatch] = useState(false);
  const [spainCountry, setSpainCountry] = useState(null);
  const { items: cartItems, setIsShippingAvailable, setShippingFee } = useCartStore();
  const { shipping_cost, shipping_cost_islands } = useCompanyStore();

  const defaultCountry = data?.country || spainCountry || null;

  const {
    watch,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: data?.first_name || '',
      last_name: data?.last_name || '',
      email: data?.email || '',
      phone: data?.phone || '',
      address: data?.address || '',
      country: defaultCountry,
      province: data?.province || null,
      locality: data?.locality || '',
      postal_code: data?.postal_code || '',
    },
  });

  useEffect(() => {
    const fetchSpain = async () => {
      if (!data?.country && !spainCountry) {
        try {
          const { status, data: countriesData } = await getCountries({ nombre: 'es', page: 1, page_size: 10 });
          if (status === 200 && countriesData) {
            const spain = countriesData.find(
              (country) =>
                country &&
                country.nombre &&
                (country.nombre.toLowerCase() === 'españa' ||
                  country.nombre.toLowerCase() === 'espana' ||
                  country.codigo === 'ES')
            );
            if (spain) {
              const formattedSpain = {
                value: spain.id,
                label: spain.nombre,
                ...spain,
              };
              setSpainCountry(formattedSpain);
              setValue('country', formattedSpain);
            }
          }
        } catch (error) {
          console.error('Error al buscar España:', error);
        }
      }
    };
    fetchSpain();
  }, [data?.country, setValue]);

  const values = watch();

  useEffect(() => {
    if (data) {
      const defaultCountryValue = data?.country || spainCountry || null;
      reset({
        first_name: data?.first_name || '',
        last_name: data?.last_name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        address: data?.address || '',
        country: defaultCountryValue,
        province: data?.province || null,
        locality: data?.locality || '',
        postal_code: data?.postal_code || '',
      });
    }
  }, [data, reset, spainCountry]);

  useEffect(() => {
    onChange(values);
  }, [JSON.stringify(values)]);

  const onSubmit = (formData) => {
    onSave(formData);
  };

  const handleLimitedDeliveries = async () => {
    try {
      const { data } = await getLimitedDeliveries();
      if (data) {
        setLimitedDeliveries(data.results);
      }
    } catch (error) {
      console.error('Error getProvinces-country', error);
    }
  };

  useEffect(() => {
    if (isShippingAddressDetails) {
      handleLimitedDeliveries();
    }
  }, [isShippingAddressDetails]);

  const fetchCountries = async (filterBy, value) => {
    if (value.length > 1) {
      const { status, data } = await getCountries({ [filterBy]: value, page: 1, page_size: 10 });
      if (status === 200) {
        return data?.map((item) => ({
          ...item,
          label: item[filterBy],
          value: item.id,
        }));
      }
    }
    return [];
  };

  const loadCountryOptions = useDebouncedCallback((inputValue, callback) => {
    if (!inputValue || inputValue.length <= 1) {
      callback([]);
      return;
    }
    fetchCountries('nombre', inputValue).then((options) => callback(options));
  }, 500);

  const handleGetProvincesCountry = async () => {
    try {
      const { data } = await getProvinces();
      if (data) {
        const formatedProvinces = data.map((prov) => ({
          value: prov.id,
          label: prov.nombre,
        }));
        setProvinces(formatedProvinces);
      }
    } catch (error) {
      console.error('Error getProvinces-country', error);
    }
  };

  useEffect(() => {
    handleGetProvincesCountry();
  }, []);

  const checkDeliveryProvince = (provinceSelected) => {
    setIsShippingAvailable({ availability: true, message: '' });
    const updatedData = { ...data, province: provinceSelected, postal_code: '' };
    onChange(updatedData);
    const fee = calculateShippingFee(provinceSelected?.label || provinceSelected?.value || '', cartItems, shipping_cost, shipping_cost_islands);
    setShippingFee(fee);
    if (isShippingAddressDetails) {
      const limitedDeliveryMatch = limitedDeliveries.find(
        (limitDelivery) => limitDelivery.province === provinceSelected.value,
      );
      setIsLimitedDeliveryMatch(limitedDeliveryMatch);
      if (limitedDeliveryMatch?.not_delivered) {
        setIsShippingAvailable({ availability: false, message: 'notDeliverProvince' });
      } else {
        setIsLimitedDeliveryMatch(false);
        setIsShippingAvailable({ availability: true, message: '' });
      }
    }
  };

  const checkDeliveryPostalCode = (postalCode) => {
    setIsShippingAvailable({ availability: true, message: '' });
    if (isShippingAddressDetails) {
      const postalCodeLimitedMatch =
        isLimitedDeliveryMatch && isLimitedDeliveryMatch?.postal_codes_list.includes(postalCode);
      if (postalCodeLimitedMatch) {
        //! NO SE REALIZA ENVIO
        setIsShippingAvailable({ availability: false, message: 'notDeliverPostalCode' });
      } else {
        if (isLimitedDeliveryMatch?.is_special_price) {
          //! ENVIO CON PRECIO ESPECIAL
          setShippingFee(isLimitedDeliveryMatch?.special_price);
        } else {
          const fee = calculateShippingFee(data?.province?.label || data?.province || '', cartItems, shipping_cost, shipping_cost_islands);
          setShippingFee(fee);
          setIsLimitedDeliveryMatch(false);
        }
      }
    } else return;
  };

  const renderButtonSaveText = () => {
    if (disableBtnSave) {
      return <Spinner animation="grow" variant="dark" />;
    } else if (isShippingAddressDetails || isBillingAddressDetails) {
      return <span>{t('saveAddressDefault').toUpperCase()}</span>;
    } else {
      return <span>{t('save').toUpperCase()}</span>;
    }
  };

  return (
    <Row>
      {additionalInfoHeader && <h4 className="mx-2 my-4">{additionalInfoHeader}</h4>}
      <Col xs={12} md={4}>
        <p className="mt-4">{t('name')}</p>
        <Controller
          name="first_name"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
          }}
          render={({ field }) => <InputCustom type="text" placeholder={t('name')} title={t('name')} {...field} />}
        />
        {errors.first_name && <div className="text-danger small mt-1">{errors.first_name.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('apellido')}</p>
        <Controller
          name="last_name"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
          }}
          render={({ field }) => (
            <InputCustom type="text" placeholder={t('apellido')} title={t('apellido')} {...field} />
          )}
        />
        {errors.last_name && <div className="text-danger small mt-1">{errors.last_name.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('correoelectronico')}</p>
        <Controller
          name="email"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: t('incorrectFormat'),
            },
          }}
          render={({ field }) => (
            <InputCustom type="text" placeholder={t('correoelectronico')} title={t('correoelectronico')} {...field} />
          )}
        />
        {errors.email && <div className="text-danger small mt-1">{errors.email.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('telefono')}</p>
        <Controller
          name="phone"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
            pattern: { value: /^[0-9]+$/, message: t('solonumeros') },
            minLength: { value: 6, message: t('minimumLength') },
            maxLength: { value: 15, message: t('maximumLength') },
          }}
          render={({ field }) => (
            <InputCustom type="text" placeholder={t('telefono')} title={t('telefono')} {...field} />
          )}
        />
        {errors.phone && <div className="text-danger small mt-1">{errors.phone.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('direccion')}</p>
        <Controller
          name="address"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
          }}
          render={({ field }) => (
            <InputCustom
              type="text"
              placeholder={t('direccion')}
              title={t('direccion')}
              isInvalid={!!errors.address}
              {...field}
            />
          )}
        />
        {errors.address && <div className="text-danger small mt-1">{errors.address.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('pais')}</p>
        <Controller
          name="country"
          control={control}
          rules={{}}
          render={({ field }) => (
            <AsyncSelect
              {...field}
              placeholder={t('pais')}
              title={t('pais')}
              className="mb-3"
              loadOptions={loadCountryOptions}
              defaultOptions={countries?.filter((country) => country && country.value && country.label) || []}
              value={field.value}
              onChange={(selected) => {
                field.onChange(selected);
                // Limpiar provincia cuando cambia el país
                setValue('province', null);
              }}
            />
          )}
        />
        {errors.country && <div className="text-danger small mt-1">{errors.country.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('province')}</p>
        <Controller
          name="province"
          control={control}
          rules={{}}
          render={({ field }) => {
            const isDisabled = !watch('country');
            const selectComponent = (
              <Select
                {...field}
                placeholder={t('province')}
                title={t('province')}
                className="mb-3"
                options={provinces}
                value={field.value}
                isDisabled={isDisabled}
                onChange={(selected) => {
                  field.onChange(selected);
                  checkDeliveryProvince(selected);
                }}
              />
            );

            return isDisabled ? (
              <OverlayTrigger overlay={<Tooltip id="province-tooltip">{t('selectCountryFirst')}</Tooltip>}>
                <div className="d-inline-block" style={{ width: '100%' }}>
                  {selectComponent}
                </div>
              </OverlayTrigger>
            ) : (
              selectComponent
            );
          }}
        />
        {errors.province && <div className="text-danger small mt-1">{errors.province.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('localidad')}</p>
        <Controller
          name="locality"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
            pattern: {
              value: /^[A-Za-zÁÉÍÓÚÑáéíóúñ\s]+$/,
              message: t('onlyLetters'),
            },
          }}
          render={({ field }) => (
            <InputCustom type="text" placeholder={t('localidad')} title={t('localidad')} {...field} />
          )}
        />
        {errors.locality && <div className="text-danger small mt-1">{errors.locality.message}</div>}
      </Col>
      <Col xs={12} md={4}>
        <p className="mt-4">{t('postalcode')}</p>
        <Controller
          name="postal_code"
          control={control}
          rules={{
            required: { value: true, message: t('camporequerido') },
            pattern: {
              value: /^[0-9]+$/,
              message: t('solonumeros'),
            },
            minLength: {
              value: 4,
              message: t('minimumLengthPostalCode'),
            },
            maxLength: {
              value: 15,
              message: t('maximumLength'),
            },
          }}
          render={({ field }) => (
            <InputCustom
              type="text"
              name="postalcode"
              placeholder={t('postalcode')}
              title={t('postalcode')}
              {...field}
              onChange={(e) => {
                field.onChange(e);
                checkDeliveryPostalCode(e.target.value);
              }}
            />
          )}
        />
        {errors.postal_code && <div className="text-danger small mt-1">{errors.postal_code.message}</div>}
      </Col>
      <Col xs={12} md={12} className="d-flex justify-content-end gap-2">
        {!hideCancelButton && (
          <Button variant="outline-secondary mt-4" onClick={onCancel}>
            {t('cancel').toUpperCase()}
          </Button>
        )}{' '}
        <Button className="blue-btn mt-4" disabled={disableBtnSave} onClick={handleSubmit(onSubmit)}>
          {renderButtonSaveText()}
        </Button>
      </Col>
    </Row>
  );
};

AddressForm.propTypes = {
  data: PropTypes.object,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  disableBtnSave: PropTypes.bool,
  hideCancelButton: PropTypes.bool,
  countries: PropTypes.array,
  additionalInfoHeader: PropTypes.string,
  isShippingAddressDetails: PropTypes.bool,
  isBillingAddressDetails: PropTypes.bool,
};

export default AddressForm;
